import React, {useState} from 'react';
import { Card, Table, Badge, Dropdown, Pagination } from 'react-bootstrap';
import {authService} from "../../services/authService";
import NotificationService from "../../services/NotificationService";
import {userService} from "../../services/userService";
import {businessYearService} from "../../services/businessYearService";
import eventEmitter from "../../shared/eventEmitter";
import {EventEmitterTypes} from "../../shared/eventEmitterEnum";
import {useNavigate} from "react-router-dom";


export interface Settings {
    created: Date,
    current: boolean,
    maximumWorkAmountPerDay: number,
    scheduleEndDate: Date,
    scheduleStartDate: Date,
    year: number
}

interface YearlySettingsTableProps {
    settings: Settings[];
}


const YearlySettingsTable: React.FC<YearlySettingsTableProps> = ({settings}) => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 5;
    const totalSettingsAmount = settings.length;
    const totalPages = Math.ceil(totalSettingsAmount / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };
    const paginationItems = [];
    for (let number = 1; number <= totalPages; number++) {
        paginationItems.push(
            <Pagination.Item
                key={number}
                active={number === currentPage}
                onClick={() => handlePageChange(number)}
                className="page-item"
            >
                {number}
            </Pagination.Item>
        );
    }
    const [isModalVisible, setIsModalVisible] = useState(false);
    
    const [sortField, setSortField] = useState('year');
    const [sortOrder, setSortOrder] = useState('desc');
    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    const sortedSettings = [...settings].sort((a, b) => {
        if (a[sortField] < b[sortField]) return sortOrder === 'asc' ? -1 : 1;
        if (a[sortField] > b[sortField]) return sortOrder === 'asc' ? 1 : -1;
        return 0;
    });
    const displayedSettings = sortedSettings.slice(startIndex, startIndex + pageSize);
    
    return (
        <>
            <div className="d-flex align-items-center justify-content-start mt-2 mb-4">
                <div className="fs-5 fst-italic">viewing {displayedSettings.length} of {totalSettingsAmount} years</div>
            </div>
            <div className="card border no-shadow">
                <div className="">
                    <table className="table table-hover table-striped align-middle m-0">
                        <thead>
                        <tr>
                            <th onClick={() => handleSort('year')}>
                                <a href="#" className="sort-icons" onClick={(e) => e.preventDefault()}>
                                      <span
                                          className={`icon ${sortField === 'year' && sortOrder === 'asc' ? 'active' : ''}`}>
                                        <i className="far fa-long-arrow-alt-up"></i>
                                      </span>
                                    <span
                                        className={`icon ${sortField === 'year' && sortOrder === 'desc' ? 'active' : ''}`}>
                                        <i className="far fa-long-arrow-alt-down"></i>
                                      </span>
                                </a>
                                Name
                            </th>
                            <th onClick={() => handleSort('current')}>
                                <a href="#" className="sort-icons" onClick={(e) => e.preventDefault()}>
                                      <span
                                          className={`icon ${sortField === 'current' && sortOrder === 'asc' ? 'active' : ''}`}>
                                        <i className="far fa-long-arrow-alt-up"></i>
                                      </span>
                                    <span
                                        className={`icon ${sortField === 'current' && sortOrder === 'desc' ? 'active' : ''}`}>
                                        <i className="far fa-long-arrow-alt-down"></i>
                                      </span>
                                </a>
                                Status
                            </th>
                            <th className="text-end">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                            {displayedSettings.map((setting) => (
                            <tr key={setting.year} className={!setting.current ? 'nullRow' : ''}>
                                <td>{setting.year} Settings</td>
                                <td>
                                    <Badge bg={!setting.current ? 'danger' : 'success'}>
                                        {!setting.current ? 'Inactive' : 'Current'}
                                    </Badge>
                                </td>
                                <td className="text-end">
                                    <Dropdown style={{display: 'flex', justifyContent: 'flex-end'}} align="end"
                                              className="text-end">
                                        <Dropdown.Toggle
                                            variant="outline-dark"
                                            size="lg"
                                            className="action-btn text-end"
                                        >
                                            <i className="far fa-ellipsis-v"></i>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item onClick={() => navigate(`details/${setting.year}`, { state: { setting } })}>
                                                <i className="fa-light fa-key fa-fw"></i> Edit {setting.year} Settings
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => {
                                                businessYearService.makeYearCurrent(setting.year).then(r => 
                                                    eventEmitter.emit(EventEmitterTypes.REFRESH_YEARLY_SETTINGS));
                                            }}>
                                                Make {setting.year} Current Year
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="row mt-4">
                <nav aria-label="table navigation" className="d-flex align-items-center justify-content-center">
                    <Pagination className="mb-0">
                        <Pagination.Item
                            onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                            className="page-item"
                        >
                            <span aria-hidden="true">«</span>
                        </Pagination.Item>
                        {paginationItems}
                        <Pagination.Item
                            onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                            className="page-item"
                        >
                            <span aria-hidden="true">»</span>
                        </Pagination.Item>
                    </Pagination>
                </nav>
            </div>
        </>
    );
};

export default YearlySettingsTable;
